import { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  Chip,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  useTheme,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import ReplayOutlinedIcon from '@material-ui/icons/ReplayOutlined';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { ResponseErrorPanel } from '@backstage/core-components';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  salsaHostingCloudAppNonprodPermission,
  salsaHostingCloudAppProdPermission,
} from '@internal/backstage-plugin-salsa-hosting-common';

const PROD_CLOUDENV_NAMES = ['production'];

type EnvSummary = {
  envName: string;
  application: { appName: string };
  desiredCount?: number;
  runningCount?: number;
  deploymentStatus?: string;
  deploymentFailureType?: string;
  deploymentFailureReason?: string;
  spotConfiguration?: { strategy?: string };
};

type EnvironmentWithEntity = {
  envSummary: EnvSummary;
  envName: string;
  entity: Entity | null;
};

type GroupedEnvironment = EnvironmentWithEntity & {
  services: { prefix: string; env: EnvironmentWithEntity }[];
};

type EnvStatus = 'running' | 'stopped' | 'deploying' | 'failed' | 'unknown';

const SERVICE_PREFIXES = ['redis-', 'solr-'] as const;

const STATUS_COLORS: Record<EnvStatus, { light: { bg: string; border: string }; dark: { bg: string; border: string } }> = {
  running:   { light: { bg: '#e6f9e6', border: '#2e7d32' }, dark: { bg: '#1a2e1a', border: '#4caf50' } },
  stopped:   { light: { bg: '#f0f0f0', border: '#a5a5a5' }, dark: { bg: '#2a2a2a', border: '#757575' } },
  deploying: { light: { bg: '#e8eaf6', border: '#3949ab' }, dark: { bg: '#1a1d2e', border: '#7986cb' } },
  failed:    { light: { bg: '#fdecea', border: '#c62828' }, dark: { bg: '#2e1a1a', border: '#ef5350' } },
  unknown:   { light: { bg: '#fff8e1', border: '#f9a825' }, dark: { bg: '#2a2517', border: '#ffb74d' } },
};

const getStatusConfig = (isDark: boolean): Record<EnvStatus, { bg: string; border: string }> => {
  const variant = isDark ? 'dark' : 'light';
  return Object.fromEntries(
    Object.entries(STATUS_COLORS).map(([k, v]) => [k, v[variant]]),
  ) as Record<EnvStatus, { bg: string; border: string }>;
};

const getStatusStyle = (status: EnvStatus, isDark: boolean) => {
  const config = getStatusConfig(isDark);
  const { bg, border } = config[status];
  return {
    color: isDark ? '#E8EAED' : '#1a1a1a',
    fontWeight: 600 as const,
    border: `2px solid ${border}`,
    backgroundColor: bg,
  };
};

const deriveStatus = (deploymentStatus: string, runningCount: number, desiredCount: number): EnvStatus => {
  if (deploymentStatus === 'COMPLETED') {
    if (runningCount === 0 && desiredCount === 0) return 'stopped';
    if (runningCount < desiredCount) return 'deploying';
    return runningCount === 0 ? 'stopped' : 'running';
  }
  if (deploymentStatus === 'IN_PROGRESS') return 'deploying';
  if (deploymentStatus === 'FAILED') return 'failed';
  return 'unknown';
};

/** Small filled or outlined circle for container count visualization */
const StatusCircle = ({ filled, color, border, emptyBorderColor, emptyBackgroundColor }: { filled: boolean; color: string; border: string; emptyBorderColor: string, emptyBackgroundColor: string }) => (
  <Box
    component="span"
    style={{
      display: 'inline-block',
      width: 18,
      height: 18,
      borderRadius: '50%',
      backgroundColor: filled ? color : emptyBackgroundColor,
      border: `2px solid ${filled ? border : emptyBorderColor}`,
    }}
  />
);

/** Stopped indicator: gray circle with diagonal line */
const StoppedCircle = () => (
  <Box
    component="span"
    style={{
      display: 'inline-block',
      width: 18,
      height: 18,
      position: 'relative',
    }}
  >
    <svg width="18" height="18" viewBox="0 0 18 18">
      <circle cx="9" cy="9" r="8" fill="none" stroke="#757575" strokeWidth="1.5" />
      <line x1="4" y1="14" x2="14" y2="4" stroke="#757575" strokeWidth="1.5" />
    </svg>
  </Box>
);

/** Row of circles with optional label inside the bubble */
const StatusCircles = ({ runningCount, desiredCount, label, failed, failureType, failureReason, href }: {
  runningCount: number;
  desiredCount: number;
  label?: string;
  failed?: boolean;
  failureType?: string;
  failureReason?: string;
  href?: string;
}) => {
  const theme = useTheme();
  const clickable = !!href;
  const bubbleStyle = {
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    gap: 3,
    border: `1.5px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 20,
    padding: '3px 8px',
    cursor: clickable ? 'pointer' as const : 'default' as const,
  };

  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    href ? <Link to={href} style={{ textDecoration: 'none' }}>{children}</Link> : <>{children}</>;

  if (desiredCount === 0 && runningCount === 0 && !failed) {
    return (
      <Wrapper>
        <Tooltip title={`${label ? label : ''}${label ? ': ' : ''}Stopped (0/0)`}>
          <Box style={bubbleStyle}>
            {label && (
              <Typography variant="caption" style={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.text.secondary, marginRight: 2 }}>
                {label}
              </Typography>
            )}
            <StoppedCircle />
          </Box>
        </Tooltip>
      </Wrapper>
    );
  }

  if (desiredCount === 0 && runningCount === 0 && failed) {
    const isDark = theme.palette.type === 'dark';
    const statusConfig = getStatusConfig(isDark);
    const detail = `${label ? (`${label  }: `) : ''}Failed (0/0)${failureType ? ` — ${failureType}` : ''}${failureReason ? `: ${failureReason}` : ''}`;
    return (
      <Wrapper>
        <Tooltip title={detail}>
          <Box style={{ ...bubbleStyle, borderColor: statusConfig.failed.border }}>
            {label && (
              <Typography variant="caption" style={{ fontSize: '0.65rem', fontWeight: 600, color: statusConfig.failed.border, marginRight: 2 }}>
                {label}
              </Typography>
            )}
            <Box
              component="span"
              style={{
                display: 'inline-block',
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: statusConfig.failed.bg,
                border: `1.5px solid ${statusConfig.failed.border}`,
              }}
            />
          </Box>
        </Tooltip>
      </Wrapper>
    );
  }

  // Circles beyond desiredCount are "overflow" (scaling down) — shown outside the border
  const overflowCount = Math.max(0, runningCount - desiredCount);
  const isFailing = !!failed;
  const failureDetail = isFailing
    ? `\n⚠ ${runningCount}/${desiredCount} running${failureType ? ` — ${failureType}` : ''}${failureReason ? `: ${failureReason}` : ''}`
    : '';
  const isDark = theme.palette.type === 'dark';
  const statusConfig = getStatusConfig(isDark);

  return (
    <Wrapper>
      <Tooltip title={`${label ? label : ''}${label ? ': ' : ''}${runningCount} running / ${desiredCount} desired${failureDetail}`}>
        <Box display="inline-flex" alignItems="center" style={{ gap: 4 }}>
          {/* Overflow circles (running > desired, being scaled down) */}
          {Array.from({ length: overflowCount }, (_, i) => (
            <StatusCircle
              key={`overflow-${i}`}
              filled
              color={statusConfig.running.bg}
              border={statusConfig.running.bg}
              emptyBorderColor={isFailing ? statusConfig.failed.border : statusConfig.stopped.border}
              emptyBackgroundColor={isFailing ? statusConfig.failed.bg : statusConfig.stopped.bg}
            />
          ))}
          {/* Desired-count zone with rounded border */}
          <Box style={bubbleStyle}>
            {label && (
              <Typography variant="caption" style={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.text.secondary, marginRight: 2 }}>
                {label}
              </Typography>
            )}
            {Array.from({ length: desiredCount }, (_, i) => {
              const actualIndex = overflowCount + i;
              const isRunning = actualIndex < runningCount;
              // ick: duplicate
              const emptyColor = isFailing ? statusConfig.failed.bg : statusConfig.deploying.bg;
              const emptyBorder = isFailing ? statusConfig.failed.border : statusConfig.deploying.border;
              return (
                <StatusCircle
                  key={`desired-${i}`}
                  filled={isRunning}
                  color={isRunning ? statusConfig.running.bg : emptyColor}
                  border={isRunning ? statusConfig.running.border : emptyBorder}
                  emptyBorderColor={isFailing ? statusConfig.failed.border : statusConfig.stopped.border}
                  emptyBackgroundColor={isFailing ? statusConfig.failed.bg : statusConfig.stopped.bg}
                />
              );
            })}
          </Box>
        </Box>
      </Tooltip>
    </Wrapper>
  );
};

const ServiceBadge = ({ prefix, env }: { prefix: string; env: EnvironmentWithEntity }) => {

  const s = env.envSummary;
  const running = s.runningCount ?? 0;
  const desired = s.desiredCount ?? 0;
  const isFailed = deriveStatus(s.deploymentStatus ?? 'FAILED', running, desired) === 'failed';
  const appName = s.application.appName;
  const envPath = `/catalog/default/cloudenv/${appName}-${s.envName}/`;

  return (
    <StatusCircles
      runningCount={running}
      desiredCount={desired}
      label={prefix}
      failed={isFailed}
      failureType={s.deploymentFailureType}
      failureReason={s.deploymentFailureReason}
      href={envPath}
    />
  );
};

const ACTION_LABELS: Record<string, string> = {
  start: 'Start',
  stop: 'Stop',
  redeploy: 'Redeploy',
};

const ActionButtons = ({ orgName, appName, envName }: { orgName: string; appName: string; envName: string }) => {

  const { fetch } = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const isProd = PROD_CLOUDENV_NAMES.includes(envName.toLowerCase());
  const permission = isProd ? salsaHostingCloudAppProdPermission : salsaHostingCloudAppNonprodPermission;
  const { allowed: canManage, loading } = usePermission({ permission, resourceRef: appName });

  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setSubmitting(true);
    try {
      const backendUrl = await discoveryApi.getBaseUrl('salsa-hosting-api');
      const response = await fetch(`${backendUrl}/orgs/${orgName}/apps/${appName}/envs/${envName}/actions/${confirmAction}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || response.statusText);
      }
      setSnackbar({ open: true, message: `${ACTION_LABELS[confirmAction]} requested for ${envName}`, severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: `Failed to ${confirmAction} ${envName}: ${e.message}`, severity: 'error' });
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  if (loading) return null;

  return (
    <>
      <Box display="flex" alignItems="center" style={{ gap: 1, opacity: canManage ? 1 : 0.35 }}>
        <Tooltip title={canManage ? 'Start' : 'Start (no permission)'}>
          <span>
            <IconButton size="small" onClick={() => canManage ? setConfirmAction('start') : setSnackbar({ open: true, message: 'You do not have permission to manage this environment', severity: 'error' })} style={{ color: canManage ? '#3baa41' : '#888888' }}>
              <PlayArrowIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canManage ? 'Stop' : 'Stop (no permission)'}>
          <span>
            <IconButton size="small" onClick={() => canManage ? setConfirmAction('stop') : setSnackbar({ open: true, message: 'You do not have permission to manage this environment', severity: 'error' })} style={{ color: canManage ? '#c62828' : '#888888' }}>
              <StopIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canManage ? 'Redeploy' : 'Redeploy (no permission)'}>
          <span>
            <IconButton size="small" onClick={() => canManage ? setConfirmAction('redeploy') : setSnackbar({ open: true, message: 'You do not have permission to manage this environment', severity: 'error' })} style={{ color: canManage ? '#1976d2' : '#888888' }}>
              <ReplayOutlinedIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Dialog open={!!confirmAction} onClose={() => !submitting && setConfirmAction(null)}>
        <DialogTitle>{confirmAction ? ACTION_LABELS[confirmAction] : ''} Environment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to <strong>{confirmAction}</strong> the <strong>{envName}</strong> environment
            {isProd && <span style={{ color: '#c62828' }}> (PRODUCTION)</span>}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" disabled={submitting}>
            {submitting ? 'Requesting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

const EnvironmentCard = ({ envData, orgName }: { envData: GroupedEnvironment; orgName: string }) => {
  const theme = useTheme();
  const envSummary = envData.envSummary;
  const envName = envSummary.envName;
  const appName = envSummary.application.appName;
  const basePath = `/catalog/default/cloudenv/${appName}-${envName}`;

  const desiredCount = envSummary.desiredCount ?? 0;
  const runningCount = envSummary.runningCount ?? 0;
  const deploymentStatus = envSummary.deploymentStatus ?? 'FAILED';
  const deploymentFailureType = envSummary.deploymentFailureType ?? '';
  const deploymentFailureReason = envSummary.deploymentFailureReason ?? '';

  const isDark = theme.palette.type === 'dark';
  const statusConfig = getStatusConfig(isDark);
  const parentStatus = deriveStatus(deploymentStatus, runningCount, desiredCount);
  const parentFailed = parentStatus === 'failed';

  // Check if any child service is failed — if so, the parent card should show failed too
  const anyChildFailed = envData.services.some(svc => {
    const s = svc.env.envSummary;
    return deriveStatus(s.deploymentStatus ?? 'FAILED', s.runningCount ?? 0, s.desiredCount ?? 0) === 'failed';
  });

  const status = anyChildFailed && parentStatus !== 'failed' ? 'failed' as EnvStatus : parentStatus;
  const borderColor = statusConfig[status].border;

  return (
    <Box
      mb={1.5}
      p={2}
      borderLeft={`12px solid ${borderColor}`}
      borderRadius={4}
      border={`1px solid ${theme.palette.divider}`}
      style={{ background: theme.palette.background.paper, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
    >
      {/* Row 1: env name + spot tags (left), circles (top right) */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Link to={`${basePath}/`} style={{ textDecoration: 'none' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {envName}
            </Typography>
          </Link>
          {/* Spot configuration tag */}
          <Chip
            label={envSummary.spotConfiguration?.strategy === 'off' ? 'on-demand' : 'spot'}
            size="small"
            style={{ fontSize: '0.7rem', fontWeight: 500 }}
          />
          {/* TODO: Add a checkbox/toggle to control whether this environment should sleep or not */}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <StatusCircles
            runningCount={runningCount}
            desiredCount={desiredCount}
            failed={parentFailed}
            failureType={deploymentFailureType}
            failureReason={deploymentFailureReason}
            href={`${basePath}/`}
          />
          {envData.services.map(svc => (
            <ServiceBadge key={svc.env.envName} prefix={svc.prefix} env={svc.env} />
          ))}
        </Box>
      </Box>
      {/* Row 2: Details / Logs / Metrics (bottom left), action controls (bottom right) */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Link to={`${basePath}/`} style={{ textDecoration: 'none' }}>
            <Button size="small" variant="outlined">Details</Button>
          </Link>
          <Link to={`${basePath}/logs`} style={{ textDecoration: 'none' }}>
            <Button size="small" variant="outlined">Logs</Button>
          </Link>
          <Link to={`${basePath}/metrics`} style={{ textDecoration: 'none' }}>
            <Button size="small" variant="outlined">Metrics</Button>
          </Link>
        </Box>
        <ActionButtons orgName={orgName} appName={appName} envName={envName} />
      </Box>
    </Box>
  );
};

const groupEnvironments = (environments: EnvironmentWithEntity[]) => {
  const parentEnvs = new Map<string, EnvironmentWithEntity>();
  const serviceEnvs: { prefix: string; parentName: string; env: EnvironmentWithEntity }[] = [];

  // First pass: identify parents vs service sub-envs
  for (const env of environments) {
    const match = SERVICE_PREFIXES.find(p => env.envName.startsWith(p));
    if (match) {
      const parentName = env.envName.slice(match.length);
      serviceEnvs.push({ prefix: match.replace(/-$/, ''), parentName, env });
    } else {
      parentEnvs.set(env.envName, env);
    }
  }

  // Second pass: attach services to parents, collect orphans
  const grouped: GroupedEnvironment[] = [];
  const orphaned: { prefix: string; env: EnvironmentWithEntity }[] = [];

  for (const [, parent] of parentEnvs) {
    grouped.push({ ...parent, services: [] });
  }

  for (const svc of serviceEnvs) {
    const parent = grouped.find(g => g.envName === svc.parentName);
    if (parent) {
      parent.services.push({ prefix: svc.prefix, env: svc.env });
    } else {
      orphaned.push({ prefix: svc.prefix, env: svc.env });
    }
  }

  return { grouped, orphaned };
};

const OrphanedCard = ({ prefix, env }: { prefix: string; env: EnvironmentWithEntity }) => {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const statusConfig = getStatusConfig(isDark);
  const status = deriveStatus(
    env.envSummary.deploymentStatus ?? 'FAILED',
    env.envSummary.runningCount ?? 0,
    env.envSummary.desiredCount ?? 0,
  );
  const borderColor = statusConfig[status].border;
  const appName = env.envSummary.application.appName;
  const basePath = `/catalog/default/cloudenv/${appName}-${env.envSummary.envName}`;

  return (
    <Box
      mb={1}
      p={1.5}
      borderLeft={`6px solid ${borderColor}`}
      borderRadius={4}
      style={{ background: statusConfig.unknown.bg, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Chip label={prefix} size="small" style={{ fontWeight: 500, fontSize: '0.7rem' }} />
          <Link to={`${basePath}/`} style={{ textDecoration: 'none' }}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {env.envName}
            </Typography>
          </Link>
        </Box>
        <Chip label={status} style={getStatusStyle(status, isDark)} size="small" />
      </Box>
    </Box>
  );
};

const EnvironmentColumns = ({ environments, orgName }: { environments: EnvironmentWithEntity[]; orgName: string }) => {
  if (environments.length === 0) {
    return <Typography color="textSecondary">No environments found</Typography>;
  }

  const { grouped, orphaned } = groupEnvironments(environments);

  const namedEnvs = grouped.filter(e => !e.envName.startsWith('pr-'));
  const prEnvs = grouped.filter(e => e.envName.startsWith('pr-'));

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {namedEnvs.length === 0 ? (
            <Typography variant="body2" color="textSecondary">{}</Typography>
          ) : (
            namedEnvs.map(env => (
              <EnvironmentCard key={env.envName} envData={env} orgName={orgName} />
            ))
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {prEnvs.length === 0 ? (
            <Typography variant="body2" color="textSecondary">{}</Typography>
          ) : (
            prEnvs.map(env => (
              <EnvironmentCard key={env.envName} envData={env} orgName={orgName} />
            ))
          )}
        </Grid>
      </Grid>
      {orphaned.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle2" style={{ color: '#f9a825', marginBottom: 8 }}>
            Orphaned Services
          </Typography>
          {orphaned.map(o => (
            <OrphanedCard key={o.env.envName} prefix={o.prefix} env={o.env} />
          ))}
        </Box>
      )}
    </>
  );
};

export const CloudAppEnvironmentsContent = () => {

  const { entity } = useEntity();
  const { fetch } = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [environments, setEnvironments] = useState<EnvironmentWithEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ error: Error | unknown, message: string }>({ error: null, message: "" });

  const appName = entity.metadata.annotations!['salsa.hosting/app-name'];
  const orgName = entity.metadata.annotations!['salsa.hosting/org-name'];
  const platform = entity.metadata.annotations!['salsa.hosting/platform'];

  useEffect(() => {

    const fetchEnvironments = async () => {

      try {

        setLoading(true);

        const backendUrl = await discoveryApi.getBaseUrl('salsa-hosting-api');
        const response = await fetch(`${backendUrl}/orgs/${orgName}/apps/${appName}/envs`);
        if (!response.ok) {
          throw new Error(`Failed to fetch environments: ${response.statusText}`);
        }
        const envSummaries: EnvSummary[] = await response.json();

        // Also get catalog entities for metadata (name, namespace) for links
        const catalogResponse = await catalogApi.getEntities({
          filter: {
            kind: 'CloudEnv',
            'metadata.annotations.salsa.hosting/app-name': appName,
          },
        });

        // Create a map of env name to entity
        const entityMap = new Map<string, Entity>();
        catalogResponse.items.forEach((r) => {
            if (r.metadata.annotations) {
                const envName = r.metadata.annotations!['salsa.hosting/env-name'];
                if (envName) {
                    entityMap.set(envName, r);
                }
            }
        });

        // Combine API data with catalog entities
        const combined = envSummaries.map((envSummary) => ({
          envSummary,
          entity: entityMap.get(envSummary.envName?.toLowerCase() || '') || null,
          envName: envSummary.envName?.toLowerCase() || ''
        }));

        setEnvironments(combined);

      } catch (e) {
        setError({ error: e, message: "Failed to load environments" });
      } finally {
        setLoading(false);
      }
    };

    if (appName) {
      fetchEnvironments();
    }
  }, [appName, fetch, orgName, catalogApi, discoveryApi]);

  const appDetail = (key: string, value: string) => (
    <Grid item xs={6} md={4}>
      <Typography variant="body2" color="textSecondary">
        {key}
      </Typography>
      <Typography variant="body1">
        {value || ''}
      </Typography>
    </Grid>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <InfoCard>
          <Grid container spacing={2}>
            { appDetail("Application Name", appName) }
            { appDetail("Organization", orgName) }
            { appDetail("Platform", platform) }
          </Grid>
        </InfoCard>
      </Grid>
      <Grid item xs={12}>
        <InfoCard title="Environments">
          {loading ? (<Typography>Loading environments...</Typography>) : <EnvironmentColumns environments={environments} orgName={orgName} /> }
          { error.error instanceof Error ? (<ResponseErrorPanel error={error.error} />) : (null) }
        </InfoCard>
      </Grid>
    </Grid>
  );
};
